import { useState } from 'react'

import { DocumentNode } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import { BACKEND_DATE_FORMAT } from 'config/Vars'
import {
  GoalsQuantitativeValue,
  PerformanceGoalsBulkCreateMetricDocument,
  PerformanceGoalsBulkCreateMetricMutationVariables,
  PerformanceGoalsCreateMetricDocument,
  PerformanceGoalsCreateMetricMutationVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import NumberField from 'modules/core/components/NumberField'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  validateAssignee,
  validateAssignees,
  validateDate,
  validateGoalExistence,
  validateTitle,
} from 'modules/performance_goals/lib/keyResultsValidations'

import { AssigneeSelectField } from '../AssigneeSelectField'
import { MetricGroupSelectField } from '../MetricGroupSelectField'

const keyResultTypes = ['quantitative', 'qualitative']
type KeyResultType = (typeof keyResultTypes)[number]

export const CreateMetricModal: React.FC<{
  onClose: () => void
  defaultValues?: Partial<PerformanceGoalsCreateMetricMutationVariables>
  refetchQueries?: DocumentNode[]
  showMetricGroupSelection?: boolean
  showEmployeeSelection?: boolean
}> = ({
  onClose,
  defaultValues,
  refetchQueries = [],
  showMetricGroupSelection,
  showEmployeeSelection,
}) => {
  const [create] = useMutation(PerformanceGoalsCreateMetricDocument, {
    extractorFn: (d) => d.goalsMutations.createGoalsMetric,
    refetchQueries,
    awaitRefetchQueries: true,
  })

  const [bulkCreate] = useMutation(PerformanceGoalsBulkCreateMetricDocument, {
    extractorFn: (d) => d.goalsMutations.bulkCreateGoalsMetric,
    refetchQueries,
    awaitRefetchQueries: true,
  })

  const [isQuantitative, setIsQuantitative] = useState(false)

  const defaultQuantitativeValue: GoalsQuantitativeValue = {
    startValue: 0,
    targetValue: 100,
    currentValue: 0,
  }

  const form = useForm<
    Omit<PerformanceGoalsCreateMetricMutationVariables, 'assigneeId'> & {
      quantitativeValue: GoalsQuantitativeValue
    } & {
      assigneeId: PerformanceGoalsCreateMetricMutationVariables['assigneeId'] | undefined | null
      assigneeIds: PerformanceGoalsBulkCreateMetricMutationVariables['assigneeIds']
    }
  >({
    defaultValues: {
      ...defaultValues,
      assigneeId: defaultValues?.assigneeId,
      startOn: defaultValues?.startOn || moment().format(BACKEND_DATE_FORMAT),
      endOn: defaultValues?.endOn || moment().add('1', 'month').format(BACKEND_DATE_FORMAT),
      title: defaultValues?.title || '',
      quantitativeValue: defaultValues?.quantitativeValue || defaultQuantitativeValue,
      assigneeIds: [],
      metricGroupId: defaultValues?.metricGroupId || '',
    },
    validate(values) {
      const errors: Partial<Record<keyof typeof values, string | undefined>> = {}

      const titleError = validateTitle(values.title)
      if (titleError) {
        errors.title = titleError
      }

      const dateError = validateDate(values.startOn, values.endOn)
      if (dateError) {
        errors.endOn = dateError
      }

      const goalError = validateGoalExistence(values.metricGroupId)
      if (goalError) {
        errors.metricGroupId = goalError
      }

      const assigneeError = !values.assigneeIds.length && validateAssignee(values.assigneeId)
      if (assigneeError) {
        errors.assigneeId = assigneeError
      }

      const assigneesError = values.assigneeIds.length && validateAssignees(values.assigneeIds)
      if (assigneesError) {
        errors.assigneeId = assigneesError
      }

      return errors
    },
    onSubmit: async (values) => {
      const { assigneeId, assigneeIds } = values
      if (showEmployeeSelection) {
        const bulkVariables = {
          ...values,
          assigneeIds: assigneeIds.map(Number),
          agreementDetails: null,
        }
        const response = await bulkCreate({
          variables: isQuantitative
            ? bulkVariables
            : {
                ...bulkVariables,
                quantitativeValue: undefined,
              },
        })
        const result = handleResult(response, () => undefined)

        if (result.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
        }
        onClose()
        return result
      }
      if (!assigneeId) throw 'Missing assignee'

      const variables: PerformanceGoalsCreateMetricMutationVariables = {
        ...values,
        assigneeId,
      }
      const result = await create({
        variables: isQuantitative
          ? variables
          : {
              ...variables,
              quantitativeValue: undefined,
            },
      })
      const handled = handleResult(result, () => null)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      onClose()
      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('performance_goals.create_key_results_modal.title')}
      description={i18n.t('performance_goals.create_key_results_modal.description')}
      onClose={onClose}
      illustration="analytics-graph-lines"
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button onClick={() => onClose()} type="secondary" disabled={form.isSubmitting}>
            {i18n.t('performance_goals.create_key_results_modal.actions.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('performance_goals.create_key_results_modal.actions.create')}
          </Button>
        </>
      }
    >
      <Box gap="s12" style={{ minWidth: '450px' }}>
        <TextField
          error={form.touched.title ? form.errors.title?.toString() : undefined}
          label={i18n.t('performance_goals.create_key_results_modal.fields.title')}
          value={form.values.title}
          onChange={(value) => form.setFieldValue('title', value)}
        />
        <Box flexDirection="row" gap="s12">
          <DayPicker
            value={form.values.startOn}
            format="ui"
            valueFormat="backend"
            label={i18n.t('performance_goals.create_key_results_modal.fields.start_on')}
            mode="single"
            onChange={(date) => form.setValues({ ...form.values, startOn: date })}
          />
          <DayPicker
            value={form.values.endOn}
            error={form.errors.endOn?.toString()}
            format="ui"
            valueFormat="backend"
            label={i18n.t('performance_goals.create_key_results_modal.fields.end_on')}
            mode="single"
            onChange={(date) => form.setValues({ ...form.values, endOn: date })}
          />
        </Box>
        <SelectField
          label={i18n.t('performance_goals.create_key_results_modal.fields.type.title')}
          value={(isQuantitative ? 'quantitative' : 'qualitative') satisfies KeyResultType}
          options={[
            {
              label: i18n.t('performance_goals.create_key_results_modal.fields.type.qualitative'),
              value: 'qualitative' satisfies KeyResultType,
            },
            {
              label: i18n.t('performance_goals.create_key_results_modal.fields.type.quantitative'),
              value: 'quantitative' satisfies KeyResultType,
            },
          ]}
          onChange={(value) => {
            setIsQuantitative(value === ('quantitative' satisfies KeyResultType))
          }}
        />
        {isQuantitative && (
          <Box gap="s12" flexDirection="row" width="full">
            <Box width="full">
              <NumberField
                label={i18n.t('performance_goals.create_key_results_modal.fields.startValue')}
                value={form.values.quantitativeValue.startValue}
                maxDecimals={2}
                onChange={(value) => {
                  form.setValues({
                    ...form.values,
                    quantitativeValue: {
                      ...form.values.quantitativeValue,
                      currentValue: value ?? 0,
                      startValue: value ?? 0,
                    },
                  })
                }}
              />
            </Box>
            <Box width="full">
              <NumberField
                label={i18n.t('performance_goals.create_key_results_modal.fields.targetValue')}
                value={form.values.quantitativeValue.targetValue}
                maxDecimals={2}
                onChange={(value) => {
                  form.setValues({
                    ...form.values,
                    quantitativeValue: {
                      ...form.values.quantitativeValue,
                      targetValue: value ?? 0,
                    },
                  })
                }}
              />
            </Box>
          </Box>
        )}
        {showMetricGroupSelection && (
          <MetricGroupSelectField
            value={form.values.metricGroupId.toString()}
            error={form.errors.metricGroupId?.toString()}
            touched={form.touched.metricGroupId}
            onChange={(metricGroupId) => {
              form.setValues({ ...form.values, metricGroupId: metricGroupId ?? '' })
            }}
          />
        )}
        {showEmployeeSelection && (
          <AssigneeSelectField
            error={form.errors.assigneeId?.toString()}
            touched={form.touched.assigneeId}
            value={form.values.assigneeIds}
            onChange={(assigneeIds) => {
              form.setValues({
                ...form.values,
                assigneeIds: assigneeIds ? assigneeIds : form.values.assigneeIds,
              })
            }}
          />
        )}
      </Box>
    </Modal>
  )
}
